'use client'

import { useEffect, useRef } from 'react'
import Cookies from 'js-cookie'
import { ensureFreshToken } from '@/lib/axios'

const parseJwtExpiry = (token: string): number | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }
    const payload = JSON.parse(atob(parts[1]))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Schedules a proactive token refresh ~60 seconds before the access token expires.
 * Prevents reactive 401 → refresh flows and race conditions during idle periods.
 * Re-schedules automatically after each successful refresh.
 */
const useTokenRefresh = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const scheduleRefresh = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      const token = Cookies.get('accessToken')
      if (!token) {
        return
      }

      const expiry = parseJwtExpiry(token)
      if (!expiry) {
        return
      }

      // Refresh 60 seconds before expiry
      const delay = Math.max(0, expiry - Date.now() - 60_000)

      timeoutRef.current = setTimeout(async () => {
        const newToken = await ensureFreshToken()
        if (newToken) {
          // Re-schedule for the newly issued token
          scheduleRefresh()
        }
      }, delay)
    }

    scheduleRefresh()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])
}

export default useTokenRefresh
